import { IContextualMenuItem, Stack } from '@fluentui/react';
import { forwardRef, HTMLAttributes, ReactElement, Ref, useEffect, useState } from 'react';

import { IComponent, IconName } from '../../models/components.models';
import { FluentClickHandler } from '../../models/types';
import { cssVarValue } from '../../utils/color-utils';
import { useClassNames, useTheme } from '../../utils/hooks';
import { BasicList } from '../BasicList/BasicList';
import { Button } from '../Button/Button';
import { buttonStylesIcon, buttonStylesIconCard, buttonStylesIconCardLiked } from '../Button/Button.styles';
import { Item } from '../Item/Item';
import { itemStylesTag } from '../Item/Item.styles';
import { TooltipHost } from '../TooltipHost/TooltipHost';
import { badgeHeaderStyle, cardStylesDefault, ICardStyles } from './Card.styles';

export interface CardBadge {
  color: `#${string}`;
  label: string;
}

export interface ICardProps<T extends { [key: string]: any }>
  extends IComponent<ICardStyles>,
    HTMLAttributes<HTMLElement> {
  actions?: JSX.Element;
  badges?: CardBadge[];
  badgesOffset?: number;
  categoryField?: keyof T;
  data: T;
  descriptionField?: keyof T;
  disableLikedButton?: boolean;
  disableMenuButton?: boolean;
  disablePinnedButton?: boolean;
  idField: keyof T;
  imagePathField: keyof T;
  likedButtonDataTest?: string;
  likedButtonTooltipText?: string;
  likedField?: keyof T;
  likesField?: keyof T;
  menuButtonDataTest?: string;
  menuButtonTooltipText?: string;
  menuItems?: IContextualMenuItem[];
  pinnedButtonDataTest?: string;
  pinnedButtonTooltipText?: string;
  pinnedField?: keyof T;
  statusColorField?: keyof T;
  statusField?: keyof T;
  titleField: keyof T;
  useImageLazyLoader?: boolean;
  userNameField?: keyof T;
  onClick?: () => void;
  onPin?: () => void;
  onLike?: () => void;
  onClickTitle?: () => void;
}

interface ICardClassNames {
  root: string;
  content: string;
  thumbnail: string;
  thumbnailHeader: string;
  thumbnailBadgeList: string;
  thumbnailHeaderDivider: string;
  thumbnailHeaderPin: string;
  thumbnailHeaderLike: string;
  thumbnailHeaderLikeLikes: string;
  thumbnailImage: string;
  thumbnailImageDescription: string;
  thumbnailImageDescriptionDetail: string;
  thumbnailStatus: string;
  header: string;
  headerTags: string;
  headerTagsList: string;
  headerTitle: string;
  headerTitleLink: string;
  headerUserName: string;
  headerActions: string;
  headerMenu: string;
}

export const Card = forwardRef(<T extends { [key: string]: any }>(props: ICardProps<T>, ref: Ref<HTMLElement>) => {
  const {
    actions,
    badges,
    badgesOffset = 12,
    categoryField,
    className,
    data,
    descriptionField,
    disableLikedButton,
    disableMenuButton,
    disablePinnedButton,
    idField = 'id' as keyof T,
    imagePathField,
    likedButtonDataTest = 'card-like-button',
    likedButtonTooltipText,
    likedField,
    likesField,
    menuButtonDataTest = 'card-menu-button',
    menuButtonTooltipText,
    menuItems,
    pinnedButtonDataTest = 'card-pin-button',
    pinnedButtonTooltipText,
    pinnedField,
    statusColorField,
    statusField,
    styles,
    titleField = 'title' as keyof T,
    useImageLazyLoader,
    userNameField,
    onClick,
    onPin,
    onLike,
    onClickTitle,
    ...elementProps
  } = props;
  const theme = useTheme();
  const [loadingPin, setLoadingPin] = useState(false);
  const [loadingLike, setLoadingLike] = useState(false);
  useEffect(() => {
    setLoadingLike(false);
    setLoadingPin(false);
  }, [data]);
  const classNames = useClassNames<Partial<ICardStyles>, ICardClassNames>('Card', cardStylesDefault, styles, className);
  const onClickPin: FluentClickHandler | undefined = onPin
    ? (e) => {
        setLoadingPin(true);
        onPin();
        e.stopPropagation();
      }
    : undefined;

  const onClickLike: FluentClickHandler | undefined = onLike
    ? (e) => {
        setLoadingLike(true);
        onLike();
        e.stopPropagation();
      }
    : undefined;

  return (
    <article {...elementProps} ref={ref} key={`${data[idField]}`} className={classNames.root}>
      <div className={classNames.content}>
        <section className={classNames.thumbnail}>
          <header className={`${classNames.thumbnailHeader}`} style={{ left: -badgesOffset }}>
            {badges && badges.length > 0 && (
              <BasicList
                data={badges}
                idField={'label'}
                labelField={'label'}
                itemRenderer={(badge, index) => (
                  <Item
                    id={`badge-${index}`}
                    data={{
                      id: `badge-${index}`,
                      isCategory: true,
                      title: `${badge.label}`,
                    }}
                    labelField="title"
                    idField="id"
                    styles={[
                      itemStylesTag,
                      badgeHeaderStyle,
                      {
                        labelButton: {
                          borderLeft: `4px solid ${badge.color}`,
                          backgroundColor: `${cssVarValue(badge.color)}30`,
                        },
                      },
                    ]}
                  />
                )}
              />
            )}
          </header>
          <div
            className={`${classNames.thumbnailImage}${
              data[imagePathField] && useImageLazyLoader ? ' image-lazy-load loading' : ''
            }${onClick ? ' clickable' : ''}`}
            style={{
              backgroundImage: data[imagePathField]
                ? useImageLazyLoader
                  ? `url('shimmer.gif')`
                  : `url('${data[imagePathField]}')`
                : `url('logo512.png')`,
            }}
            data-src={data[imagePathField] ? `${data[imagePathField]}` : 'logo512.png'}
            onClick={onClick}
          >
            {descriptionField && (
              <div className={classNames.thumbnailImageDescription}>
                <div className={classNames.thumbnailImageDescriptionDetail}>{String(data[descriptionField])}</div>
              </div>
            )}
          </div>
          {statusField && (
            <div
              className={classNames.thumbnailStatus}
              style={{
                backgroundColor: `${statusColorField ? data[statusColorField] : theme.palette?.green200}`,
                left: -badgesOffset,
              }}
            >
              {String(data[statusField])}
            </div>
          )}
        </section>
        <header className={classNames.header}>
          {categoryField && (
            <small className={classNames.headerTags} title={`${data[categoryField]}`}>
              <span className={classNames.headerTagsList} role="list">
                {String(data[categoryField])}
              </span>
            </small>
          )}
          <h3
            className={`${classNames.headerTitle} ${onClickTitle ? `${classNames.headerTitleLink}` : ''}`}
            title={`${data[titleField]}`}
            onClick={onClickTitle}
          >
            {String(data[titleField])}
          </h3>
          <Stack
            horizontal
            horizontalAlign="start"
            tokens={{ childrenGap: 5 }}
            className={`${classNames.thumbnailBadgeList}`}
          >
            {pinnedField && (
              <div className={classNames.thumbnailHeaderPin} onClick={onClickPin}>
                <TooltipHost content={pinnedButtonTooltipText} hidden={!pinnedButtonTooltipText}>
                  <Button
                    styles={[buttonStylesIcon, buttonStylesIconCard]}
                    iconName={data[pinnedField] ? IconName.PinnedSolid : IconName.Pinned}
                    title="Pin"
                    ariaLabel="Pin"
                    loading={loadingPin}
                    onClick={onClickPin}
                    disabled={disablePinnedButton}
                    data-test={pinnedButtonDataTest}
                  />
                </TooltipHost>
              </div>
            )}
            {likedField && (
              <div className={classNames.thumbnailHeaderLike} onClick={disableLikedButton ? undefined : onClickLike}>
                <TooltipHost content={likedButtonTooltipText} hidden={!likedButtonTooltipText}>
                  <Button
                    styles={
                      data[likedField]
                        ? [buttonStylesIcon, buttonStylesIconCard, buttonStylesIconCardLiked]
                        : [buttonStylesIcon, buttonStylesIconCard]
                    }
                    iconName={data[likedField] ? IconName.HeartFill : IconName.Heart}
                    title="Like"
                    ariaLabel="Like"
                    loading={loadingLike}
                    onClick={onClickLike}
                    disabled={disableLikedButton}
                    data-test={likedButtonDataTest}
                  />
                </TooltipHost>
                {likesField && (
                  <div
                    className={classNames.thumbnailHeaderLikeLikes}
                    style={{
                      color: `${
                        disableLikedButton ? theme.semanticColors?.textDisabled : theme.semanticColors?.textQuinary
                      }`,
                    }}
                  >
                    {String(data[likesField])}
                  </div>
                )}
              </div>
            )}
            {!!menuItems && menuItems.length && (
              <div className={classNames.headerMenu}>
                {
                  <TooltipHost content={menuButtonTooltipText} hidden={!menuButtonTooltipText}>
                    <Button
                      styles={[buttonStylesIcon, buttonStylesIconCard, { root: { minWidth: '32px' } }]}
                      menuIconName={IconName.More}
                      menuItems={menuItems}
                      disabled={disableMenuButton}
                      data-test={menuButtonDataTest}
                    />
                  </TooltipHost>
                }
              </div>
            )}
          </Stack>
          {!!userNameField && <p className={classNames.headerUserName}>{String(data[userNameField])}</p>}
          {!!actions && <div className={classNames.headerActions}>{actions}</div>}
        </header>
      </div>
    </article>
  );
}) as <T extends { [key: string]: any }>(props: ICardProps<T> & { ref?: Ref<HTMLElement> }) => ReactElement;
